import React, { useState, useRef, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import EditIcon from '@mui/icons-material/Edit';

interface JSONEditorProps<T = Record<string, any>> {
  value: T;
  onChange: (value: T) => void;
  editable?: boolean;
  title?: string;
  titleAction?: React.ReactNode;
  saveOnBlur?: boolean;
}

export const JSONEditor = <T extends Record<string, any> = Record<string, any>>({
  value,
  onChange,
  editable = true,
  title = 'Operation Details',
  titleAction,
  saveOnBlur = true
}: JSONEditorProps<T>) => {
  const [isEditing, setIsEditing] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Initialize JSON text when entering edit mode
  useEffect(() => {
    if (isEditing) {
      setJsonText(JSON.stringify(value, null, 2));
      setError(null);
      // Focus textarea when entering edit mode
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  }, [isEditing, value]);

  const handleStartEdit = () => {
    if (editable) {
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setJsonText('');
    setError(null);
  };

  const handleSave = () => {
    try {
      const parsed = JSON.parse(jsonText);
      onChange(parsed);
      setIsEditing(false);
      setJsonText('');
      setError(null);
    } catch (err) {
      setError('Invalid JSON. Please check your syntax.');
      console.error('JSON parse error:', err);
    }
  };

  const handleBlur = () => {
    // Only auto-save on blur if there's no error
    if (!error && jsonText) {
      handleSave();
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Typography variant="subtitle2" style={{ fontWeight: '600' }}>
            {title}
          </Typography>
          {titleAction}
        </div>
        {editable && !isEditing && (
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={handleStartEdit}
            style={{ minWidth: 'auto' }}
          >
            Edit JSON
          </Button>
        )}
      </div>
      
      {isEditing ? (
        <div style={{ 
          backgroundColor: '#fff',
          border: '2px solid #1976d2',
          borderRadius: '4px',
          padding: '12px',
          maxHeight: '300px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <textarea
            ref={textareaRef}
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              setError(null);
            }}
            onBlur={saveOnBlur ? handleBlur : undefined}
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              lineHeight: '1.4',
              border: 'none',
              outline: 'none',
              resize: 'vertical',
              minHeight: '200px',
              width: '100%',
              padding: '8px',
              backgroundColor: '#fafafa'
            }}
          />
          {error && (
            <Typography variant="caption" style={{ color: '#d32f2f' }}>
              {error}
            </Typography>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button
              size="small"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!!error}
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ 
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          padding: '12px',
          maxHeight: '300px',
          overflowY: 'auto',
          fontFamily: 'monospace',
          fontSize: '12px',
          lineHeight: '1.4'
        }}>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(value, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};
